import { FC } from 'react';

import { useFormik } from 'formik';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { fileSystemService } from 'v2/api/fileSystemService';
import { FolderForm } from 'v2/components/fileSystem/form';
import { Nullable } from 'v2/types';
import { FolderModel } from 'v2/types/FileSystemModel';
import { handleHttpError } from 'v2/utils';

interface NestedFolderDialogProps {
  folder: FolderModel;
  isOpen: boolean;
  onClose: VoidFunction;
  onSuccess: (isRoot: boolean, folderId?: Nullable<string>) => void;
}

export const NestedFolderDialog: FC<NestedFolderDialogProps> = ({ folder, isOpen, onClose, onSuccess }) => {
  const { trigger, isMutating } = useSWRMutation('createFolder', fileSystemService.createFolder, {
    onSuccess: (newFolder) => {
      toast.success('The nested folder has been successfully created');
      onSuccess(newFolder.isRoot, folder.id);
      handleClose();
    },
    onError: handleHttpError,
  });

  const formik = useFormik<FolderForm>({
    initialValues: { name: '', description: '' },
    onSubmit: (values) => {
      trigger({ ...values, parentId: folder.id });
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Create Nested Folder</DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent className="form">
          <TextField
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Name"
            required
            fullWidth
          />

          <TextField
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Description"
            multiline
            rows={4}
            fullWidth
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button type="submit" variant="contained" loading={isMutating}>
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
